<script setup lang="ts">
import IconButton from '@ui/components/icon/IconButton.vue'
import { ButtonVariant } from '@ui/styles'
import { DVH, DVW } from '@ui/utils/supportsCSS'
import Times from '~icons/cue-regular/times'
import Backdrop from '../backdrop/Backdrop.vue'

const props = withDefaults(
  defineProps<{
    label?: string
    text?: string
    full?: boolean
    close?: boolean
    type?: 'mini' | 'full'
    maxWidth?: string
    maxHeight?: string
    height?: string
    actionsLine?: boolean
    lock?: boolean
    useVShow?: boolean
  }>(),
  {
    label: '',
    text: '',
    full: true,
    close: true,
    type: 'mini',
    maxWidth: `100${DVW}`,
    maxHeight: `100${DVH}`,
    height: 'unset',
    actionsLine: false,
    lock: false,
    useVShow: false,
  },
)

defineSlots<{
  'header'?: () => void
  'header-bottom'?: () => void
  'content'?: () => void
  'actions'?: () => void
}>()

const show = defineModel<boolean>('show', { default: false })

const style = computed(() => ({
  'max-width': `min(calc(100${DVW} - 16px), ${props.maxWidth}`,
  'max-height': `min(calc(100${DVH} - 16px), ${props.maxHeight}`,
  'height': props.height,
}))
</script>

<template lang="pug">
Backdrop(v-model:show="show" :closable="!lock" :useVShow="useVShow")
  .modal-dialog.flex.col-0.w-100p(:class="type" :style="style")
    .header-container.typ-18-22-500.flex.col-8.p-t-20(:class="{ grey: $slots['header-bottom'] }")
      .header.flex-between-center.p-x-24
        .header-label.text-ellipsis
          slot(name="header")
            | {{ $t(label) }}
        IconButton(v-if="close" :name="Times" :variant="ButtonVariant.Function" :disabled="lock" @click="show = false")
      slot(name="header-bottom")

    .content-container.flex.h-100p
      slot(name="content")
        | {{ $t(text) }}

    .actions-container.flex-between-center.gap-10.p-8-24-24(v-if="$slots.actions" :class="{ line: actionsLine }")
      slot(name="actions")
</template>

<style scoped>
.modal-dialog {
  overflow: hidden;
  border: 1px solid var(--cue-border-neutral-primary);
  background: var(--cue-bg-surface-primary);
  box-shadow: 0 1px 20px 0 color-mix(in srgb, var(--cue-static-neutral-inverse) 20%, transparent);
  min-width: 304px;

  &.mini {
    --uno: br-24;
  }
}

.header-label {
  color: var(--cue-content-neutral-primary);
}

.header-container {
  &.grey {
    background: var(--cue-bg-neutral-primary-hover);
  }
}

.content-container {
  overflow: auto;
}

.actions-container {
  &.line {
    border-top: 1px solid var(--cue-border-neutral-secondary);
  }
}
</style>
